// noinspection GraphQLUnresolvedReference

import {gql, TypedDocumentNode} from "@apollo/client";
import {
    ARTICLE_FRAGMENT,
    BRAND_FRAGMENT,
    BULLET_POINT_FRAGMENT,
    CONTACT_GROUP_FRAGMENT,
    DISCLAIMER_FRAGMENT,
    ENVIRONMENTAL_CLAIMS_FRAGMENT,
    GUIDE_FRAGMENT,
    IMAGE_FRAGMENT,
    ITEM_QUESTION_CONNECTION_FRAGMENT,
    ITEM_QUESTIONS_STATISTICS_FRAGMENT,
    ITEM_RATINGS_STATISTICS_FRAGMENT,
    ITEM_REVIEW_MEDIA_CONNECTION_FRAGMENT,
    PRICE_FRAGMENT,
    PRODUCT_CALLOUT_FRAGMENT,
    PRODUCT_CROSS_SELL_FRAGMENT,
    PRODUCT_DYNAMIC_DIMENSIONS_FRAGMENT,
    PRODUCT_ENVIRONMENTAL_FRAGMENT,
    PRODUCT_FRAGMENT,
    PRODUCT_MEDIA_FRAGMENT,
    PRODUCT_OPTIONS_FRAGMENT,
    PRODUCT_RECOMMENDATION_FRAGMENT,
    PRODUCT_RESOURCES_FRAGMENT,
    PRODUCT_SPECIFICATION_FRAGMENT,
    PROMOTION_FRAGMENT,
    RATING_PAGINATION_FRAGMENT,
    RATING_STATISTICS_FRAGMENT,
    STOCK_INFORMATION_FRAGMENT,
    YOUTUBE_VIDEO_FRAGMENT
} from "./fragments";
import {
    CollectionResponseType,
    ComparisonTableResponseType,
    CompatibleWithResponseType,
    ContactGroupType,
    ContactGroupVariablesType,
    CrossSellType,
    ImageType,
    Nullable,
    OffCanvasProductType,
    OffCanvasVariableType,
    PageInfoType,
    PriceType,
    ProductPriceVariablesType,
    ProductPricingVariablesType,
    ProductRecommendationsResponseType,
    ProductResponseType,
    ProductVariablesType,
    PromotionType,
    QueryListsResponseType,
    QueryListsVariablesType,
    QueryMediaVariablesType,
    QueryRatingsResponseType,
    QueryRatingsVariablesType,
    QuestionEdgeType,
    RatingStatisticsType,
    RecommendationVariablesType,
    ReviewMediaType,
    SpecificationResponseType,
    StockInformationType
} from "./types";
import {ItemClassificationType, RecommendationType} from "./enums";

// noinspection GraphQLSchemaValidation
export const QUERY_PRODUCT: TypedDocumentNode<{ product: Nullable<ProductResponseType> }, ProductVariablesType> = gql`
    ${BRAND_FRAGMENT}
    ${GUIDE_FRAGMENT}
    ${IMAGE_FRAGMENT}
    ${PRICE_FRAGMENT}
    ${PRODUCT_FRAGMENT}
    ${ARTICLE_FRAGMENT}
    ${DISCLAIMER_FRAGMENT}
    ${BULLET_POINT_FRAGMENT}
    ${YOUTUBE_VIDEO_FRAGMENT}
    ${PRODUCT_MEDIA_FRAGMENT}
    ${PRODUCT_CALLOUT_FRAGMENT}
    ${PRODUCT_OPTIONS_FRAGMENT}
    ${STOCK_INFORMATION_FRAGMENT}
    ${PRODUCT_RESOURCES_FRAGMENT}
    ${PRODUCT_CROSS_SELL_FRAGMENT}
    ${ENVIRONMENTAL_CLAIMS_FRAGMENT}
    ${PRODUCT_ENVIRONMENTAL_FRAGMENT}
    ${ITEM_RATINGS_STATISTICS_FRAGMENT}
    ${ITEM_QUESTIONS_STATISTICS_FRAGMENT}
    ${PRODUCT_DYNAMIC_DIMENSIONS_FRAGMENT}
    query GetProduct($productId: Int!, $currencyCode: String!, $isMobile: Boolean!) {
        product: getProduct(productId: $productId) {
            ...ProductFragment
            ...ResourcesFragment
            ...ProductOptionsFragment
            ...ProductEnvironmentFragment
            ...ProductDynamicDimensionsFragment
            ...ProductCalloutFragment
            ...StockInformationFragment
            articles {
                ...ArticleFragment
            }
            primaryArticles {
                ...ArticleFragment
            }
            brand {
                ...BrandFragment
            }
            bulletPoints(isMobile: $isMobile) {
                ...BulletPointFragment
            }
            disclaimers {
                ...DisclaimerFragment
            }
            environmentalClaims {
                ...EnvironmentalClaimsFragment
            }
            guides {
                ...GuideFragment
            }
            primaryGuides {
                ...GuideFragment
            }
            media {
                ...ProductMediaFragment
            }
            price(currencyCode: $currencyCode) {
                ...PriceFragment
            }
            primaryMedia {
                ...ProductMediaFragment
            }
            primaryBulletPoints(isMobile: $isMobile) {
                ...BulletPointFragment
            }
            replacementParts(currencyCode: $currencyCode, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                ...CrossSellFragment
            }
            ratings(visible: true) {
                ...ItemRatingsStatisticsFragment
            }
            primaryRatings(visible: true) {
                ...ItemRatingsStatisticsFragment
            }
            questions(visible: true) {
                ...ItemQuestionsStatisticsFragment
            }
            primaryQuestions(visible: true) {
                ...ItemQuestionsStatisticsFragment
            }
            youTubeVideos(take: 10) {
                ...YouTubeVideoFragment
            }
            primaryYouTubeVideos(take: 10) {
                ...YouTubeVideoFragment
            }
        }
    }
`;

export const QUERY_PRODUCT_PRICING: TypedDocumentNode<{
    product: {
        prices: Nullable<PriceType>[]
    }
}, ProductPricingVariablesType> = gql`
    ${PRICE_FRAGMENT}
    query GetProductPricing($productId: Int!, $currencyCode: String!, $width: Float, $height: Float) {
        product: getProduct(productId: $productId) {
            prices(currencyCode: $currencyCode, width: $width, height: $height) {
                ...PriceFragment
            }
        }
    }
`;

export const QUERY_PRODUCT_PRICE: TypedDocumentNode<{
    product: {
        price: Nullable<PriceType>
    }
}, ProductPriceVariablesType> = gql`
    ${PRICE_FRAGMENT}
    query GetProductPrice($productId: Int!, $currencyCode: String!, $quantity: Int!, $width: Float, $height: Float) {
        product: getProduct(productId: $productId) {
            price(currencyCode: $currencyCode, quantity: $quantity, width: $width, height: $height) {
                ...PriceFragment
            }
        }
    }
`;

export const QUERY_CONTACT_GROUP: TypedDocumentNode<{
    contactGroup: Nullable<ContactGroupType>
}, ContactGroupVariablesType> = gql`
    ${CONTACT_GROUP_FRAGMENT}
    query GetContactGroup($catalogId: ID!, $type: String!) {
        contactGroup: getContactGroup(input: { catalogId: $catalogId, type: $type }) {
            ...ContactGroupFragment
        }
    }
`;

export const QUERY_PRODUCT_PROMOTION: TypedDocumentNode<{ promotion: Nullable<PromotionType> }, {
    productId: number
}> = gql`
    ${PROMOTION_FRAGMENT}
    query GetProductPromotion($productId: Int!) {
        promotion: getProductPromotion(productId: $productId) {
            ...PromotionFragment
        }
    }
`;

export const QUERY_RECENTLY_VIEWED: TypedDocumentNode<{
    product: Nullable<{
        collectionName?: string,
        designerId?: number,
        id: number,
        isDynamicDimension: boolean,
        primaryId?: number,
        sku: string
        title: string,
        variantPath: string
        media: {
            images: {
                dot4b: Nullable<ImageType>[]
            }
        },
        price?: PriceType,
        ratings: Nullable<{
            id: number,
            type: ItemClassificationType,
            stats: Nullable<RatingStatisticsType>
        }>,
        primaryRatings: Nullable<{
            id: number,
            type: ItemClassificationType,
            stats: Nullable<RatingStatisticsType>
        }>,
        stockInformation: Nullable<{
            id: number,
            minQuantity: number
        }>
    }>
}, RecommendationVariablesType> = gql`
    ${PRICE_FRAGMENT}
    ${ITEM_RATINGS_STATISTICS_FRAGMENT}
    query ProductRecommendationsQuery($id: Int!, $currencyCode: String!) {
        product: getProduct(productId: $id) {
            id
            primaryId
            designerId
            sku
            title
            variantPath
            collectionName
            isDynamicDimension
            isDynamicDimension
            media {
                itemId
                itemType
                images {
                    itemId
                    itemType
                    dot4b(take: 1) {
                        url
                        alt
                        title
                    }
                }
            }
            price(currencyCode: $currencyCode) {
                ...PriceFragment
            }
            ratings(visible: true) {
                ...ItemRatingsStatisticsFragment
            }
            primaryRatings(visible: true) {
                ...ItemRatingsStatisticsFragment
            }
            stockInformation {
                id
                minQuantity: minSaleQuantity
            }
        }
    }`;

export const QUERY_STOCK_INFORMATION: TypedDocumentNode<{
    product: {
        isTruckShipment: boolean,
        stockInformation: Nullable<StockInformationType>
    }
}, ProductVariablesType> = gql`
    ${STOCK_INFORMATION_FRAGMENT}
    query GetStockInformation($productId: Int!) {
        product: getProduct(productId: $productId) {
            ...StockInformationFragment
        }
    }
`;

export const QUERY_QUESTIONS: TypedDocumentNode<{
    userContent: {
        questions: Nullable<{
            connection: Nullable<{
                edges: Nullable<QuestionEdgeType>[]
                pageInfo: PageInfoType
            }>
        }>,
        primaryQuestions: Nullable<{
            connection: Nullable<{
                edges: Nullable<QuestionEdgeType>[]
                pageInfo: PageInfoType
            }>
        }>
    }
}, {
    id: number,
    type: ItemClassificationType,
    productId: number,
    last: number,
    before?: string
}> = gql`
    ${ITEM_QUESTION_CONNECTION_FRAGMENT}
    query QuestionsConnectionQuery($productId: Int!, $last: Int!, $before: String) {
        userContent: getProduct(productId: $productId) {
            id
            questions(visible: true) {
                id
                type
                connection(last: $last, before: $before) {
                    ...QuestionConnectionFragment
                }
            }
            primaryQuestions(visible: true) {
                id
                type
                connection(last: $last, before: $before) {
                    ...QuestionConnectionFragment
                }
            }
        }
    }
`;

export const QUERY_MEDIA: TypedDocumentNode<{
    userContent: {
        reviewMedia: Nullable<{
            connection: Nullable<{
                media: Nullable<ReviewMediaType>[]
                pageInfo: PageInfoType
            }>
        }>,
        primaryReviewMedia: Nullable<{
            connection: Nullable<{
                media: Nullable<ReviewMediaType>[]
                pageInfo: PageInfoType
            }>
        }>
    }
}, QueryMediaVariablesType> = gql`
    ${ITEM_REVIEW_MEDIA_CONNECTION_FRAGMENT}
    query ReviewMediaConnectionQuery($productId: Int!, $first: Int, $after: String, $last: Int, $before: String) {
        userContent: getProduct(productId: $productId) {
            id
            reviewMedia(visible: true) {
                id
                type
                connection(first: $first, after: $after, last: $last, before: $before) {
                    ...ReviewMediaConnectionFragment
                }
            }
            primaryReviewMedia(visible: true) {
                id
                type
                connection(first: $first, after: $after, last: $last, before: $before) {
                    ...ReviewMediaConnectionFragment
                }
            }
        }
    }
`;

export const QUERY_RATINGS: TypedDocumentNode<QueryRatingsResponseType, QueryRatingsVariablesType> = gql`
    ${RATING_PAGINATION_FRAGMENT}
    query GetProductRatingStatistics($productId: Int!, $pageSize: Int, $pageNumber: Int, $searchTerm: String, $sortBy: String, $filters: RatingFiltersInput) {
        userContent: getProduct(productId: $productId) {
            id
            ratings(visible: true) {
                id
                type
                pagination(size: $pageSize, number: $pageNumber, searchTerm: $searchTerm, sortBy: $sortBy, filters: $filters) {
                    ...RatingPaginationFragment
                }
            }
            primaryRatings(visible: true) {
                id
                type
                pagination(size: $pageSize, number: $pageNumber, searchTerm: $searchTerm, sortBy: $sortBy, filters: $filters) {
                    ...RatingPaginationFragment
                }
            }
        }
    }
`;

export const QUERY_LISTS: TypedDocumentNode<QueryListsResponseType, QueryListsVariablesType> = gql`
    query GetUserListsAndItemMatches($accountId: Int!, $productId: Int!) {
        matchingListIds: getItemUserLists(input: { accountId: $accountId, productId: $productId, active: true })
        lists: getUserLists(input: { accountId: $accountId, active: true }) {
            id
            name
            path
        }
    }
`;

export const QUERY_COMPATIBLE_WITH: TypedDocumentNode<{
    product: Nullable<CompatibleWithResponseType>
}, Pick<ProductVariablesType, 'productId' | 'currencyCode'>> = gql`
    ${PRODUCT_CROSS_SELL_FRAGMENT}
    query GetCompatibleProducts($productId: Int!, $currencyCode: String!) {
        product: getProduct(productId: $productId) {
            compatibleWith(currencyCode: $currencyCode, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                ...CrossSellFragment
            }
        }
    }
`;

export const QUERY_SPECIFICATIONS: TypedDocumentNode<{
    product: Nullable<SpecificationResponseType>
}, Pick<ProductVariablesType, 'productId'>> = gql`
    ${PRODUCT_SPECIFICATION_FRAGMENT}
    query GetSpecifications($productId: Int!) {
        product: getProduct(productId: $productId) {
            specifications {
                ...ProductSpecificationsFragment
            }
        }
    }
`;

export const QUERY_COLLECTION: TypedDocumentNode<{
    product: Nullable<CollectionResponseType>
}, Pick<ProductVariablesType, 'productId' | 'currencyCode'>> = gql`
    ${IMAGE_FRAGMENT}
    ${PRICE_FRAGMENT}
    ${ITEM_RATINGS_STATISTICS_FRAGMENT}
    query GetProductCollection($productId: Int!, $currencyCode: String!) {
        product: getProduct(productId: $productId) {
            collectionName
            collectionProducts(first: 15) {
                id
                products {
                    id
                    primaryId
                    title
                    sku
                    collectionName
                    variantPath
                    designerId
                    isDynamicDimension
                    media {
                        itemId
                        itemType
                        images {
                            itemId
                            itemType
                            dot4b(take: 1) {
                                ...ImageFragment
                            }
                        }
                    }
                    price(currencyCode: $currencyCode) {
                        ...PriceFragment
                    }
                    ratings(visible: true) {
                        ...ItemRatingsStatisticsFragment
                    }
                    primaryRatings(visible: true) {
                        ...ItemRatingsStatisticsFragment
                    }
                }
            }
        }
    }
`;

export const QUERY_PRODUCT_RECOMMENDATIONS: TypedDocumentNode<{
    product: Nullable<ProductRecommendationsResponseType>
}, {
    productId: number
    currencyCode: string
    recommendationType: RecommendationType
}> = gql`
    ${PRODUCT_RECOMMENDATION_FRAGMENT}
    query GetProductRecommendations($productId: Int!, $currencyCode: String!, $recommendationType: String!) {
        product: getProduct(productId: $productId) {
            recommendations(type: $recommendationType, currencyCode: $currencyCode, first: 16, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                id
                products {
                    ...RecommendationFragment
                }
            }
        }
    }
`;

export const QUERY_COMPARISON_TABLE: TypedDocumentNode<{
    product: Nullable<ComparisonTableResponseType>
}, Pick<ProductVariablesType, 'productId' | 'currencyCode'>> = gql`
    ${RATING_STATISTICS_FRAGMENT}
    query GetProductComparisonTable($productId: Int!, $currencyCode: String!) {
        product: getProduct(productId: $productId) {
            id
            comparisonTable(currencyCode: $currencyCode) {
                id
                productId
                primaryId
                currencyCode
                specifications {
                    typeId
                    typeName
                    unit
                    valueDisplayMode
                }
                items {
                    productId
                    primaryId
                    designerId
                    sku
                    title
                    customizeIt
                    path
                    imageSrc
                    imageAlt
                    imageTitle
                    dimensionalPrice
                    price
                    unitPrice
                    onSale
                    salePrice
                    saleAmount
                    salePercentage
                    ratingStatistics {
                        ...RatingStatisticsFragment
                    }
                    types {
                        typeId
                        typeName
                        unit
                        valueDisplayMode
                        values {
                            valueId
                            valueName
                        }
                    }
                }
            }
        }
    }
`;

export const QUERY_EMPLOYEE_NAVIGATION: TypedDocumentNode<{
    product: Nullable<{
        id: number,
        primaryId?: number,
        sku: string,
        primarySku?: string,
        quantity: number,
        targetKeyPhrase?: string,
        reportingClassName?: string,
        environmentalScore?: number,
        rank: number,
        launchDate: Date,
        stockInformation: Nullable<{
            id: number,
            status?: string,
            quantityOnHand: number,
            inTransitDays?: number,
            inTransitQuantity?: number
            isInTransit: boolean,
            isAutoOffline: boolean,
            isBuiltToOrder: boolean,
            isHardMinimumOrder: boolean,
            isInStock: boolean,
            isNewProduct: boolean,
            isOutOfStockNoBackOrder: boolean,
            leadIn: number,
            leadOut: number
        }>
    }>
}, Pick<ProductVariablesType, 'productId'>> = gql`
    query GetEmployeeNavigation($productId: Int!) {
        product: getProduct(productId: $productId) {
            id
            primaryId
            sku
            primarySku
            quantity
            targetKeyPhrase
            reportingClassName
            environmentalScore
            rank
            launchDate
            stockInformation {
                id
                status
                quantityOnHand
                inTransitDays
                inTransitQuantity
                isInTransit
                isAutoOffline
                isBuiltToOrder
                isHardMinimumOrder
                isInStock
                isNewProduct
                isOutOfStockNoBackOrder
                leadIn
                leadOut
            }
        }
    }
`;

export const QUERY_OFF_CANVAS: TypedDocumentNode<{
    product: Nullable<OffCanvasProductType>
}, OffCanvasVariableType> = gql`
    ${PRODUCT_RECOMMENDATION_FRAGMENT}
    ${PRODUCT_RESOURCES_FRAGMENT}
    query GetOffCanvasInformation($productId: Int!, $recommendationType: String!, $currencyCode: String!, $quantity: Int!, $width: Float, $height: Float) {
        product: getProduct(productId: $productId) {
            artworkGuidelineTypeId
            id
            sku
            title
            variantUrl
            price(currencyCode: $currencyCode, quantity: $quantity, width: $width, height: $height) {
                id
                dimensionalPrice
                height
                highRange
                lowRange
                onSale
                price
                productId
                saleAmount
                salePercentage
                salePrice
                unitPrice
                width
            }
            variantAttributes {
                key
                unit
                value
            }
            media {
                images {
                    dot4b(take: 1) {
                        id
                        type
                        width
                        height
                        url
                        zoomUrl
                        thumbnailUrl
                        title
                        previewUrl
                        isWide
                        isRaw
                        filename
                        description
                        alt
                    }
                    itemId
                    itemType
                }
                itemId
                itemType
            }
            recommendations(type: $recommendationType, currencyCode: $currencyCode, first: 16, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                id
                products {
                    ...RecommendationFragment
                }
            }
            stockInformation {
                id
                artworkLeadTime
                isInStock
                leadIn
                leadOut
                quantityOnHand
                isInTransit
                inTransitDays
                minQuantity: minSaleQuantity
            }
            ...ResourcesFragment
        }
    }
`;

export const QUERY_HIGH_LEVEL_CALLOUT: TypedDocumentNode<{
    product: Nullable<{
        bundledWith: Nullable<CrossSellType>[],
        collectionName?: string,
        compatibleWith: Nullable<CrossSellType>[],
        designerId?: number,
        isDynamicDimension: boolean
        media: { images: { dot4b: Nullable<ImageType>[] } },
        price?: PriceType,
        sku: string,
        stockInformation: Nullable<StockInformationType>,
        title?: string,
        variantPath: string,
        worksWellWith: Nullable<CrossSellType>[]
    }>
}, {
    productId: number,
    currencyCode: string
}> = gql`
    ${PRICE_FRAGMENT}
    ${PRODUCT_CROSS_SELL_FRAGMENT}
    ${PRODUCT_DYNAMIC_DIMENSIONS_FRAGMENT}
    query GetHighLevelCallout($productId: Int!, $currencyCode: String!) {
        product: getProduct(productId: $productId) {
            ...ProductDynamicDimensionsFragment
            id
            sku
            title
            artworkGuidelineType
            artworkGuidelineTypeId
            installationConsultation
            componentDescription
            longDescription
            shortDescription
            collectionName
            stockInformation {
                minQuantity: minSaleQuantity
            }
            media {
                itemId
                itemType
                images {
                    itemId
                    itemType
                    dot4b(take: 2) {
                        url
                        alt
                        title
                    }
                }
            }
            bundledWith(currencyCode: $currencyCode, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                ...CrossSellFragment
            }
            price(currencyCode: $currencyCode) {
                ...PriceFragment
            }
            compatibleWith(currencyCode: $currencyCode, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                ...CrossSellFragment
            }
            worksWellWith(currencyCode: $currencyCode, find: {online:true, validated:true, pricing:true, inStock:true, salableOnline:true, hardMinimum:false}) {
                ...CrossSellFragment
            }
        }
    }
`;